import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";

const ChapterNo = ({ no }) => (
  <span aria-hidden="true" className="font-display italic text-gold/60 text-6xl md:text-8xl leading-none select-none">
    {no}
  </span>
);

const ArchImage = ({ src, alt, testid, className = "", landscape = false, children }) => (
  <div className={`relative ${className}`}>
    <div className={`absolute -inset-4 ${landscape ? "rounded-[2.8rem]" : "rounded-t-full rounded-b-[2.5rem]"} border border-gold/60`} aria-hidden="true" />
    <div className={`absolute -inset-4 ${landscape ? "rounded-[2.8rem]" : "rounded-t-full rounded-b-[2.5rem]"} border border-gold/25 scale-[1.04]`} aria-hidden="true" />
    <div className={`relative ${landscape ? "rounded-[2.2rem] aspect-[4/3]" : "rounded-t-full rounded-b-[2rem] aspect-[3/4]"} overflow-hidden border-[5px] border-white shadow-2xl shadow-teal/20 w-full`}>
      <img src={src} alt={alt} data-testid={testid} loading="lazy"
        className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-700" />
      {children}
    </div>
  </div>
);

const Annotation = ({ x, y, n, label, line, chip, testid }) => (
  <div className="absolute" style={{ left: `${x}%`, top: `${y}%` }} data-testid={testid}>
    <span className="absolute -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-gold ring-2 ring-cream shadow-md grid place-items-center text-[9px] font-bold text-teal-deep" aria-hidden="true">{n}</span>
    <span className={`absolute left-0 top-0 h-px bg-gold origin-left hidden md:block ${line}`} aria-hidden="true" />
    <span className={`absolute ${chip} hidden md:block bg-teal/90 backdrop-blur-sm text-cream text-xs leading-snug rounded-lg px-2.5 py-1.5 shadow-lg`}>
      {label}
    </span>
  </div>
);

const TIMELINE = [
  { year: "1932", who: "Mario Morelli", img: "/family-mario.webp", text: "Mario opens the parlour on the Broadstairs promenade — and a love affair between ice cream and the British seaside begins." },
  { year: "1957", who: "Giuseppe & Annita Morelli", text: "Mario's son Giuseppe takes the helm, running the parlour side by side with his wife Annita." },
  { year: "1972", who: "Marino & Bita Morelli", text: "Marino succeeds his father Giuseppe. With his wife Bita beside him, he spends over fifty years building the family business into an empire — making Morelli's what it is today." },
  { year: "2016", who: "Bibi Morelli", img: "/family-bibi.webp", text: "Marino and Bita's daughter Bibi takes over and does an incredible job carrying the brand onto the world stage — from Broadstairs all the way to Dubai and Tashkent." },
  { year: "2026", who: "Giuseppe Morelli", img: "/family-giuseppe.webp", text: "Giuseppe — son of Nito Morelli, Bibi's brother, and grandson of Marino and Bita — succeeds the business as the sixth generation, while his brother Massimo makes the ice cream fresh downstairs every day." },
];

const FLAVOURS = [
  { src: "/flavour-vanilla.webp", name: "Signature Soft Vanilla", note: "Made to perfection" },
  { src: "/flavour-pistachio.webp", name: "San Teodoro Pistachio", note: "Made to perfection" },
  { src: "/flavour-hazelnut.webp", name: "Piemonte Hazelnut", note: "Made to perfection" },
  { src: "/hero-sundae.webp", name: "Sundaes & Sorbets", note: "Made to perfection" },
];

const ARCHIVE = [
  { src: "/archive-team-door.webp", alt: "The Morelli's parlour team gathered in the doorway under the Ice Cream Parlor sign", caption: "The team at No. 14 — soda fountain days", tilt: "-1.2deg", offset: false },
  { src: "/archive-team-lineup.webp", alt: "The parlour staff lined up outside the frontage in their whites", caption: "Front of house, ready for the summer rush", tilt: "1.2deg", offset: true },
  { src: "/archive-night-front.webp", alt: "The Morelli's parlour lit up at night in the mid-twentieth century", caption: "The parlour lit up for the evening crowds, mid-century", tilt: "-0.8deg", offset: false },
  { src: "/archive-children.webp", alt: "A crowd of children enjoying Morelli's ice cream outside the parlour", caption: "Generations of young regulars — some still come in today", tilt: "0.8deg", offset: true },
];

const Story = () => {
  const heroRef = useRef(null);
  const bannerRef = useRef(null);
  const { scrollYProgress: heroP } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const { scrollYProgress: bannerP } = useScroll({ target: bannerRef, offset: ["start end", "end start"] });
  const heroImgY = useTransform(heroP, [0, 1], ["0%", "18%"]);
  const heroTextY = useTransform(heroP, [0, 1], ["0%", "45%"]);
  const bannerImgY = useTransform(bannerP, [0, 1], ["-10%", "10%"]);

  return (
    <div className="bg-cream text-teal font-body" data-testid="story-page">

      {/* ---------- Hero ---------- */}
      <section ref={heroRef} className="relative h-[92vh] min-h-[560px] overflow-hidden flex items-end" data-testid="story-hero">
        <motion.img src="/viking-bay.webp" alt="Viking Bay, Broadstairs — golden sand and beach huts" style={{ y: heroImgY }}
          className="absolute inset-0 w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-teal-deep/90 via-teal-deep/40 to-teal-deep/20" aria-hidden="true" />
        <motion.div style={{ y: heroTextY }} className="relative max-w-7xl mx-auto px-6 md:px-10 pb-24 w-full [text-shadow:0_2px_14px_rgba(20,8,40,0.55)]">
          <Reveal>
            <p className="text-xs md:text-sm uppercase tracking-[0.4em] text-gold font-medium">Over a century of magic</p>
            <h1 className="font-display font-bold tracking-tight leading-[0.95] text-5xl md:text-7xl text-cream mt-5">
              Our <span className="italic text-gold">Story</span>
            </h1>
            <p className="mt-6 text-cream/85 max-w-xl text-base md:text-lg leading-relaxed">
              Six generations, one family parlour on the Kent coast, and a love affair
              between ice cream and the British seaside that has never faded.
            </p>
          </Reveal>
        </motion.div>
        <motion.a href="#story-family" data-testid="story-scroll-hint" aria-label="Scroll to the story"
          animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          className="absolute bottom-8 right-8 w-12 h-12 rounded-full border border-cream/40 grid place-items-center text-cream hover:bg-gold hover:border-gold hover:text-teal-deep transition-colors duration-300">
          <ArrowDown size={18} />
        </motion.a>
      </section>

      {/* ---------- Chapter 1: The Family ---------- */}
      <section id="story-family" className="py-24 md:py-32 bg-blush/20 relative overflow-hidden" data-testid="story-chapter-family">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5 lg:col-start-2">
            <Reveal>
              <ArchImage src="/parlour-vintage.webp" landscape alt="The original Morelli's parlour interior in the mid-twentieth century" testid="story-family-image">
                <Annotation x={20} y={22} n={1} label="Venetian wallpaper — remnants now framed in the shop" line="hidden" chip="top-7 left-0 -translate-x-1/2 w-40 text-center" testid="annotation-wallpaper" />
                <Annotation x={60} y={42} n={2} label="The back-bar mirror — still in place today" line="hidden" chip="bottom-7 left-0 -translate-x-1/2 w-40 text-center" testid="annotation-mirror" />
                <Annotation x={46} y={54} n={3} label="The old soda fountain — retired now, once the heart of it all" line="hidden" chip="top-8 right-4 w-40" testid="annotation-fountain" />
                <Annotation x={80} y={84} n={4} label="Lloyd Loom chairs — woven in 1957, still sat on today" line="hidden" chip="bottom-7 right-0 w-40 text-right" testid="annotation-chairs" />
              </ArchImage>
              <div className="mt-5 grid grid-cols-1 gap-2.5 md:hidden" data-testid="story-annotations-legend">
                {[
                  "Venetian wallpaper — remnants now framed in the shop",
                  "The back-bar mirror — still in place today",
                  "The old soda fountain — retired now, once the heart of it all",
                  "Lloyd Loom chairs — woven in 1957, still sat on today",
                ].map((l, i) => (
                  <p key={l} className="flex items-start gap-2.5 text-xs text-teal/75 leading-snug">
                    <span className="mt-0.5 w-4 h-4 shrink-0 rounded-full bg-gold ring-1 ring-cream text-teal-deep text-[8px] font-bold grid place-items-center">{i + 1}</span>
                    {l}
                  </p>
                ))}
              </div>
              <p className="mt-8 text-center font-display italic text-teal/60 text-sm">Spot the details we still treasure today</p>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <div className="flex items-end gap-5">
              <ChapterNo no="01" />
              <SectionHeading overline="The Family" title="Six Generations of Morelli's" />
            </div>
            <Reveal delay={0.15}>
              <p className="mt-10 text-base md:text-lg leading-relaxed text-teal/80">
                It began in 1907, when the Morelli family first poured their craft into
                ice cream — recipes written by hand, then passed from parent to child,
                guarded and gently perfected across six generations.
              </p>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-teal/80">
                From Mario&rsquo;s first Broadstairs parlour, through Marino and Bita&rsquo;s
                fifty-year empire and Bibi&rsquo;s leap onto the world stage, this is the
                family line that made Morelli&rsquo;s what it is today.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="relative mt-24 max-w-5xl mx-auto" data-testid="story-timeline">
          <div className="absolute left-5 md:left-1/2 top-2 bottom-2 w-px bg-gold/50 md:-translate-x-1/2" aria-hidden="true" />
          {TIMELINE.map((t, i) => (
            <Reveal key={t.year} delay={i * 0.08} className="relative pl-14 md:pl-0 mb-12 md:mb-16 last:mb-0">
              <span className="absolute left-5 md:left-1/2 top-2.5 -translate-x-1/2 w-3 h-3 rotate-45 bg-gold border-2 border-cream" aria-hidden="true" />
              <div className={`md:w-[calc(50%-3.5rem)] ${i % 2 === 0 ? "md:mr-auto md:text-right" : "md:ml-auto"}`} data-testid={`story-timeline-${t.year}`}>
                <div className={t.img ? `flex gap-5 items-start ${i % 2 === 0 ? "md:flex-row-reverse" : ""}` : ""}>
                  {t.img && (
                    <img src={t.img} alt={`${t.who}`} loading="lazy" data-testid={`story-timeline-img-${t.year}`}
                      className="w-20 md:w-24 shrink-0 rounded-t-full rounded-b-xl border-[3px] border-white shadow-lg shadow-teal/15 aspect-[3/4] object-cover object-top" />
                  )}
                  <div>
                    <p className="font-display italic text-4xl md:text-5xl text-gold leading-none">{t.year}</p>
                    <p className="mt-3 font-display font-bold text-xl md:text-2xl text-teal">{t.who}</p>
                    <p className="mt-3 text-sm md:text-base leading-relaxed text-teal/75">{t.text}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-20 max-w-6xl mx-auto grid md:grid-cols-12 gap-8 md:gap-10 items-start" data-testid="story-generations">
          <Reveal className="md:col-span-7">
            <figure>
              <div className="border-[6px] border-white shadow-xl shadow-teal/15 rotate-[0.6deg] hover:rotate-0 transition-transform duration-500">
                <img src="/family-generations.webp" alt="Four generations of Morelli men together — Mario, Giuseppe, Marino and a young Nito" data-testid="story-generations-img" loading="lazy" className="w-full aspect-[16/9] object-cover" />
              </div>
              <figcaption className="mt-4 text-center font-display italic text-teal/70">
                Four generations in one frame — Mario, Giuseppe, Marino, and a young Nito (also known as Marino)
              </figcaption>
            </figure>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-5 md:mt-16">
            <figure>
              <div className="border-[6px] border-white shadow-xl shadow-teal/15 rotate-[-0.8deg] hover:rotate-0 transition-transform duration-500">
                <img src="/family-men-today.webp" alt="Marino and Nito Morelli with the young Giuseppe and Massimo" data-testid="story-generations-today-img" loading="lazy" className="w-full aspect-[16/10] object-cover" />
              </div>
              <figcaption className="mt-4 text-center font-display italic text-teal/70">
                Marino and Nito with the boys — Giuseppe and his brother Massimo, who makes the ice cream fresh downstairs every day
              </figcaption>
            </figure>
          </Reveal>
          <Reveal delay={0.2} className="md:col-span-8 md:col-start-3">
            <figure>
              <div className="border-[6px] border-white shadow-xl shadow-teal/15 rotate-[0.5deg] hover:rotate-0 transition-transform duration-500">
                <img src="/family-team-today.webp" alt="The whole Morelli's family and team outside the parlour today" data-testid="story-team-today-img" loading="lazy" className="w-full aspect-[8/7] md:aspect-[16/9] object-cover" />
              </div>
              <figcaption className="mt-4 text-center font-display italic text-teal/70">
                The whole family and team today — including Chris Chalklin and Sao Graciano, who have both been here for over fifty years
              </figcaption>
            </figure>
          </Reveal>
        </div>

        <div className="mt-24 max-w-6xl mx-auto" data-testid="story-archive">
          <Reveal className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.35em] text-teal/60 font-medium">From the archive</p>
            <div className="mt-4 h-px w-16 bg-gold mx-auto" />
          </Reveal>
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
            {ARCHIVE.map((p, i) => (
              <Reveal key={p.src} delay={0.1 + i * 0.08} className={p.offset ? "md:mt-12" : ""}>
                <figure>
                  <div className="border-[6px] border-white shadow-xl shadow-teal/15 hover:rotate-0 transition-transform duration-500" style={{ rotate: p.tilt }}>
                    <img src={p.src} alt={p.alt} data-testid={`story-archive-${i}`} loading="lazy" className="w-full aspect-[6/5] object-cover" />
                  </div>
                  <figcaption className="mt-4 text-center font-display italic text-teal/70">{p.caption}</figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      {/* ---------- Chapter 2: Broadstairs ---------- */}
      <section className="relative py-32 md:py-44 overflow-hidden" data-testid="story-chapter-broadstairs">
        <div ref={bannerRef} className="absolute inset-0">
          <motion.img src="/experience-vintage-exterior.webp" alt="The vintage Morelli's parlour frontage with its giant ice cream cone"
            style={{ y: bannerImgY }} className="w-full h-[120%] object-cover" />
          <div className="absolute inset-0 bg-teal-deep/45" aria-hidden="true" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 flex lg:justify-end">
          <Reveal className="bg-cream p-8 md:p-12 max-w-xl border border-gold/30 shadow-xl shadow-teal-deep/30 rounded-[2rem]">
            <div className="flex items-end gap-5">
              <ChapterNo no="02" />
              <div>
                <p className="text-xs tracking-[0.35em] uppercase font-medium text-teal/70">Broadstairs</p>
                <h2 className="font-display font-bold tracking-tight leading-[0.95] text-3xl md:text-5xl text-teal mt-3">Home on the Promenade</h2>
                <div className="mt-5 h-px w-24 bg-gold" />
              </div>
            </div>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-teal/80">
              In 1932 the family opened the doors of the parlour at 14 Victoria
              Parade — and Broadstairs has never quite been the same since. Two minutes
              from the golden sand of Viking Bay, the parlour became part of the town&rsquo;s
              rhythm: Charles Dickens festivals, summer holidays, first dates and
              four-legged regulars.
            </p>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-teal/80">
              Grandparents who once queued here as children now bring their own
              grandchildren. That, more than anything, is what Morelli&rsquo;s is.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---------- Chapter 3: The Craft ---------- */}
      <section className="py-24 md:py-32" data-testid="story-chapter-craft">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end gap-5 mb-14">
            <ChapterNo no="03" />
            <SectionHeading overline="The Craft" title="Churned With Devotion" />
          </div>
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <Reveal delay={0.1} className="lg:col-span-6">
              <div className="bg-mint/40 rounded-[2rem] border border-gold/25 p-8 md:p-10">
                <p className="text-base md:text-lg leading-relaxed text-teal/80">
                  Every Ice cream is still churned with the same devotion to craft: fresh
                  dairy, time-honoured recipes and a dash of seaside magic. One hundred
                  and nineteen years on, the ritual is unchanged — make it properly, or
                  don&rsquo;t make it at all.
                </p>
                <p className="mt-6 text-base md:text-lg leading-relaxed text-teal/80">
                  Today it is Massimo — sixth generation — who makes every batch fresh
                  downstairs each day. It is why our Art Deco parlour remains one of the
                  UK&rsquo;s most famous and beloved ice cream destinations.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.2} className="lg:col-span-6 lg:mt-16">
              <div className="rounded-[2rem] overflow-hidden border-[5px] border-white shadow-xl shadow-teal/15 aspect-[4/5]">
                <img src="/craft-churning.webp" alt="The ice cream being churned fresh in the Morelli's kitchen" data-testid="story-craft-image-2" loading="lazy"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- Chapter 4: The Flavours ---------- */}
      <section className="py-24 md:py-32 bg-blush/20 overflow-hidden" data-testid="story-chapter-flavours">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end gap-5 mb-6">
            <ChapterNo no="04" />
            <SectionHeading overline="The Flavours" title="Eighteen Reasons to Return" />
          </div>
          <Reveal delay={0.1}>
            <p className="text-base md:text-lg leading-relaxed text-teal/80 max-w-2xl mb-14">
              More than eighteen flavours fill the display each day — from our Signature
              Soft Vanilla to San Teodoro Pistachio, Piemonte Hazelnut and bright vegan
              sorbets of Sorrento lemon and Venezuelan chocolate.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {FLAVOURS.map((f, i) => (
              <Reveal key={f.name} delay={i * 0.1} className={i % 2 === 1 ? "lg:mt-14" : ""}>
                <div className="group" data-testid={`story-flavour-${i}`}>
                  <div className="rounded-t-full rounded-b-[1.5rem] overflow-hidden border-4 border-gold/40 shadow-lg shadow-teal/10 aspect-[3/4]">
                    <img src={f.src} alt={f.name} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <p className="font-display italic text-lg text-teal mt-4 text-center">{f.name}</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-teal/55 text-center mt-1">{f.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Closing CTA ---------- */}
      <section className="relative py-28 md:py-36 bg-teal-deep text-cream overflow-hidden" data-testid="story-cta">
        <img src="/parlour-night.webp" alt="" aria-hidden="true" loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <p className="text-xs md:text-sm uppercase tracking-[0.4em] text-gold font-medium">The next chapter is yours</p>
            <h2 className="font-display font-bold tracking-tight leading-[0.95] text-4xl md:text-6xl mt-5 max-w-3xl">
              Come taste the <span className="italic text-gold">history.</span>
            </h2>
            <p className="mt-6 text-cream/80 max-w-xl text-base md:text-lg leading-relaxed">
              14 Victoria Parade, Broadstairs — open every day from 8 in the
              morning until 5 in the evening. The coffee is on
              early, the sundaes run all day.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-10 flex flex-wrap gap-4">
            <a href="/#menu" data-testid="story-cta-menu"
              className="rounded-full bg-gold text-teal-deep text-xs uppercase tracking-[0.2em] font-semibold px-8 py-4 hover:bg-cream transition-colors duration-300 active:scale-95 flex items-center gap-2">
              View the Menu <ArrowRight size={15} />
            </a>
            <a href="/#visit" data-testid="story-cta-visit"
              className="rounded-full border border-cream/40 text-cream text-xs uppercase tracking-[0.2em] px-8 py-4 hover:border-gold hover:text-gold transition-colors duration-300 active:scale-95">
              Plan a Visit
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Story;
